import { hasMinimumLength, isValidFilter, compareByTitle } from '@/utils/resultsHelper';
import urls, {
  codeUrl, endpoints, userUrl, relativeUrl,
} from '@/constants/urls';
import { camundaTasklistUrl } from '@/requests/camundaHelper';
import { isAllowed } from './typeAheadSearch';

const Destinations = ({
  currentUser,
  newCohortCode,
  newDatasetCode,
  newCollectionCode,
  taskLinkEnabled,
  newRequestCode,
  newUserGroupCode,
}) => [
  {
    name: 'datasets',
    url: relativeUrl(urls.DATASETS),
    keywords: ['datasets', 'dataset'],
    permissions: [['datasets.read']],
  },
  {
    name: 'home',
    url: relativeUrl(urls.HOME),
    keywords: ['home', 'start'],
  },
  {
    name: 'search',
    url: relativeUrl(urls.SEARCH),
    keywords: ['search', 'find', 'discover'],
    permissions: [['search.run']],
  },
  {
    name: 'searches',
    url: relativeUrl(urls.SEARCHES),
    keywords: ['search', 'find', 'discover', 'searches'],
    permissions: [['search.read']],
  },
  {
    name: 'activities',
    url: relativeUrl(urls.ACTIVITIES),
    keywords: ['activity', 'activities', 'event', 'events'],
    permissions: [['activities.read']],
  },
  {
    name: 'cohorts',
    url: relativeUrl(urls.COHORTS),
    keywords: ['select', 'query', 'join', 'filter', 'cohort', 'dataset'],
    permissions: [['cohorts.read']],
  },
  {
    name: 'cohort.new',
    url: codeUrl(urls.COHORT, newCohortCode),
    keywords: ['cohort', 'query', 'select', 'new', 'create'],
    permissions: [['cohorts.create']],
  },
  {
    name: 'dataset.new',
    url: codeUrl(urls.DATASET, newDatasetCode),
    keywords: ['import', 'upload', 'dataset', 'new', 'create'],
    permissions: [['datasets.create', 'datasets.read']],
  },
  {
    name: 'requests',
    url: relativeUrl(urls.REQUESTS),
    keywords: ['request', 'access', 'dataset'],
    permissions: [['requests.read']],
  },
  {
    name: 'request.new',
    url: codeUrl(urls.REQUEST, newRequestCode),
    keywords: ['request', 'access'],
    permissions: [['requests.create']],
  },
  {
    name: 'review',
    url: relativeUrl(urls.REVIEW),
    keywords: ['request', 'access', 'dataset', 'approve', 'review'],
    permissions: [['requests.review']],
  },
  {
    name: 'themes',
    url: relativeUrl(urls.THEMES),
    keywords: ['theme', 'style', 'colour', 'color', 'preference', 'admin'],
    permissions: [['themes.update']],
  },
  {
    name: 'vocabularies',
    url: relativeUrl(urls.VOCABULARIES),
    keywords: ['link', 'label', 'text', 'vocab', 'preference', 'admin'],
    permissions: [['vocabularies.update']],
  },
  {
    name: 'roles',
    url: relativeUrl(urls.ROLES),
    keywords: ['role', 'security', 'rbac', 'permission', 'admin'],
    permissions: [['roles.read']],
  },
  ...(!currentUser.isGuest ? [{
    name: 'profile',
    url: userUrl({ uuid: currentUser.id }),
    keywords: ['me', 'profile'],
  }] : []),
  {
    name: 'users',
    url: relativeUrl(urls.USERS),
    keywords: ['user', 'profile', 'approve', 'rbac', 'role', 'admin'],
    permissions: [['users.read']],
  },
  {
    name: 'usergroups.new',
    url: codeUrl(urls.USER_GROUP, newUserGroupCode),
    keywords: ['users', 'groups', 'usergroups', 'rbac', 'role', 'admin'],
    permissions: [['usergroups.create']],
  },
  {
    name: 'usergroups',
    url: relativeUrl(urls.USER_GROUPS),
    keywords: ['groups', 'usergroups', 'new', 'rbac', 'role', 'admin'],
    permissions: [['usergroups.read']],
  },
  {
    name: 'tokens',
    url: relativeUrl(urls.TOKENS),
    keywords: ['api', 'program', 'token', 'auth', 'admin'],
    permissions: [['tokens.read']],
  },
  {
    name: 'my.tokens',
    url: relativeUrl(urls.MY_TOKENS),
    keywords: ['api', 'program', 'token', 'auth'],
    permissions: [['tokens.read']],
  },
  {
    name: 'audit',
    url: relativeUrl(urls.AUDIT),
    keywords: ['audit', 'log', 'trace', 'monitor', 'admin'],
    permissions: [['audit.read']],
  },
  {
    name: 'api',
    url: endpoints.DOCS,
    keywords: ['swagger', 'doc', 'api', 'program'],
    target: 'api',
  },
  {
    name: 'privacy',
    url: urls.PRIVACY_POLICY,
    keywords: ['privacy', 'personal', 'governance'],
    target: 'privacy',
  },
  {
    name: 'collections',
    url: relativeUrl(urls.COLLECTIONS),
    keywords: ['collections', 'collection'],
    permissions: [['datasets.read']],
  },
  {
    name: 'collection.new',
    url: codeUrl(urls.COLLECTION, newCollectionCode),
    keywords: ['collection', 'new', 'create'],
    permissions: [['collections.create', 'datasets.read']],
  },
  {
    name: 'tasks',
    url: relativeUrl(urls.TASKS),
    keywords: ['tasks', 'task'],
    permissions: [['tasks.read']],
  },
  ...(taskLinkEnabled
    ? [{
      name: 'workflow',
      url: camundaTasklistUrl(),
      keywords: ['workflow', 'tasklist'],
      target: 'camunda',
    }]
    : []),
];

export const externalLink = (link, linksName) => ({
  name: link.name,
  url: link.href,
  keywords: [linksName.toLowerCase(), link.name.toLowerCase(), 'link'],
  target: link.target,
});

export const filterUrls = (items, { query, userPermissions }) => items.filter(
  ({ keywords, permissions }) => keywords.some(
    (keyword) => keyword.includes(query.toLowerCase()) && isAllowed(permissions, userPermissions),
  ),
);

export const linkOnClick = (url, target, navigate) => (
  target ? () => (window.open(url, target)) : () => navigate(url)
);

export const externalLinkOnClick = (url, target) => (
  target ? () => (window.open(url, target)) : () => window.open(url, '_self')
);

export const destinationUrl = (destination, navigate, intl) => ({
  title: intl.formatMessage({ id: `search.goto.${destination.name}.title` }),
  description: intl.formatMessage({ id: `search.goto.${destination.name}.description` }),
  onClick: linkOnClick(destination.url, destination.target, navigate),
});

export const linkUrl = (link, navigate, intl) => ({
  title: link.name,
  description: intl.formatMessage({ id: 'search.goto.external.description' }),
  onClick: externalLinkOnClick(link.url, link.target, navigate),
});

export const destinationUrls = (navigate, state, intl) => filterUrls(Destinations(state), state)
  .map((destination) => destinationUrl(destination, navigate, intl));

export const externalLinkUrls = (navigate, query, linksName, links, intl, filter = true) => {
  if (linksName === '' || !Array.isArray(links) || links.length === 0) {
    return [];
  }

  const items = links.map((link) => externalLink(link, linksName));

  return filter ? filterUrls(items, { query }).map((link) => linkUrl(link, navigate, intl))
    : items.map((link) => linkUrl(link, navigate, intl));
};

export const termsLinkUrl = (navigate, query, termsLink, intl) => filterUrls([{
  name: 'terms',
  url: termsLink,
  keywords: ['term', 'condition', 'governance'],
  target: 'terms',
}], { query }).map((link) => destinationUrl(link, navigate, intl));

export const feedbackLinkUrl = (navigate, query, feedbackLink, intl) => filterUrls([{
  name: 'feedback',
  url: feedbackLink,
  keywords: ['feedback', 'comment'],
  target: 'feedback',
}], { query }).map((link) => destinationUrl(link, navigate, intl));

export const supportLinkUrl = (navigate, query, intl) => filterUrls([{
  name: 'support',
  url: urls.SERVICE_DESK,
  keywords: ['support', 'service', 'desk', 'help'],
  target: 'support',
}], { query }).map((link) => destinationUrl(link, navigate, intl));

export const linkResults = (navigate, { query, termsLink, feedbackLink }, intl) => {
  const linkJoin = termsLinkUrl(navigate, query, termsLink, intl).concat(supportLinkUrl(navigate, query, intl));
  if (hasMinimumLength(feedbackLink)) {
    linkJoin.push(...feedbackLinkUrl(navigate, query, feedbackLink, intl));
  }

  return linkJoin;
};

export const urlResults = (navigate, state, intl) => {
  const results = isValidFilter(state.query, { requiresFilter: true, minFilterLength: 2 })
    ? destinationUrls(navigate, state, intl).concat(linkResults(navigate, state, intl)).sort(compareByTitle)
    : [];

  return (results.length === 0) ? null
    : ({ urls: { name: intl.formatMessage({ id: 'search.goto.category' }), results } });
};

export const linksResults = (navigate, { query, linksName, links }, intl, filter = true) => {
  const valid = !filter || isValidFilter(query, { requiresFilter: true, minFilterLength: 2 });
  const results = valid ? externalLinkUrls(navigate, query, linksName, links, intl, filter).sort(compareByTitle) : [];
  return (results.length === 0) ? null : ({ urls: { name: linksName, results } });
};
